import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { CheckCircle2 } from "lucide-react";

const cls = "w-full rounded-xl border-2 border-[#E2EBF0] px-3 py-2.5 text-sm focus:border-[#008057] outline-none bg-white";
const GENDERS = ["Male", "Female", "Prefer not to say"];
const PARTICIPANT_FIELDS = ["first_name", "last_name", "dob", "gender", "tshirt_size", "shorts_size", "socks_size"];
const CONSENTS = [["guardian_confirm", "I am the participant's parent or authorized legal guardian."], ["terms", "I accept the camp's registration terms and conditions."], ["privacy", "I acknowledge the privacy policy."], ["medical_share", "I acknowledge medical info may be shared with authorized camp staff for safety."]];

function formError(g, p, c) {
  if (!g.first_name || !g.last_name) return "Please complete guardian details.";
  if (!g.email) return "Guardian email is required.";
  if (PARTICIPANT_FIELDS.some((f) => !p[f])) return "Please complete all participant fields.";
  if (!(c.guardian_confirm && c.terms && c.privacy && c.medical_share)) return "Please accept all required consents.";
  return null;
}

function useSponsoredInvite(token) {
  const [ctx, setCtx] = useState(null);
  const [done, setDone] = useState(null);
  const [p, setP] = useState({ medical: { none: true, details: "" } });
  useEffect(() => {
    api.get(`/sponsored/${token}`).then(({ data }) => {
      setCtx(data);
      if (data.already_completed) setDone("already");
      if (data.nominee) setP((x) => ({ ...x, first_name: data.nominee.first_name || "", last_name: data.nominee.last_name || "" }));
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invitation not found."));
  }, [token]);
  return { ctx, done, setDone, p, setP };
}

function SizeSelect({ label, options, onChange, testid }) {
  return <select data-testid={testid} className={cls} onChange={(e) => onChange(e.target.value)}><option value="">{label}</option>{options.map((s) => <option key={s}>{s}</option>)}</select>;
}

function ParticipantFields({ p, setP, sizes }) {
  const set = (patch) => setP({ ...p, ...patch });
  return (
    <div>
      <div className="font-display font-bold text-lg text-[#03281B] mb-3">Participant</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="sp-participant-first" className={cls} placeholder="First name" value={p.first_name || ""} onChange={(e) => set({ first_name: e.target.value })} />
        <input data-testid="sp-participant-last" className={cls} placeholder="Last name" value={p.last_name || ""} onChange={(e) => set({ last_name: e.target.value })} />
        <input data-testid="sp-participant-dob" type="date" className={cls} onChange={(e) => set({ dob: e.target.value })} />
        <SizeSelect testid="sp-gender" label="Gender" options={GENDERS} onChange={(v) => set({ gender: v })} />
        <SizeSelect testid="sp-tshirt" label="T-shirt size" options={sizes.tshirt} onChange={(v) => set({ tshirt_size: v })} />
        <SizeSelect testid="sp-shorts" label="Shorts size" options={sizes.shorts} onChange={(v) => set({ shorts_size: v })} />
        <SizeSelect testid="sp-socks" label="Socks size" options={sizes.socks} onChange={(v) => set({ socks_size: v })} />
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm"><input type="checkbox" className="accent-[#008057]" checked={p.medical?.none} onChange={(e) => set({ medical: { none: e.target.checked, details: e.target.checked ? "" : p.medical?.details } })} /> No medical considerations or allergies</label>
      {!p.medical?.none && <textarea className={`${cls} mt-2`} placeholder="Medical / allergy details" onChange={(e) => set({ medical: { none: false, details: e.target.value } })} />}
    </div>
  );
}

function GuardianFields({ g, setG }) {
  return (
    <div>
      <div className="font-display font-bold text-lg text-[#03281B] mb-3">Parent / Legal Guardian</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {[["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"], ["phone", "Phone"]].map(([k, ph]) => (
          <input key={k} data-testid={`sp-guardian-${k}`} className={cls} placeholder={ph} onChange={(e) => setG({ ...g, [k]: e.target.value })} />
        ))}
      </div>
    </div>
  );
}

function DoneScreen({ done }) {
  return (
    <div><Navbar /><div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="sponsored-done">
      <CheckCircle2 className="mx-auto text-[#008057]" size={64} />
      <h1 className="font-display text-3xl font-extrabold text-[#03281B] mt-6">Registration Complete</h1>
      <p className="text-slate-600 mt-2">{done === "already" ? "This sponsored registration has already been completed." : <>Your child's sponsored place is confirmed. Reference <b>{done}</b>. Nothing to pay - the camp fee is covered by the sponsor.</>}</p>
    </div></div>
  );
}

export default function SponsoredRegister() {
  const { token } = useParams();
  const { ctx, done, setDone, p, setP } = useSponsoredInvite(token);
  const [g, setG] = useState({});
  const [c, setC] = useState({});
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const err = formError(g, p, c);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try { const { data } = await api.post(`/sponsored/${token}/complete`, { guardian: g, participant: p, consents: c }); setDone(data.reference); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!ctx) return <div><Navbar /><div className="max-w-xl mx-auto px-4 py-24 text-center text-slate-500">Loading...</div></div>;
  if (done) return <DoneScreen done={done} />;

  return (
    <div><Navbar />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="rounded-xl bg-[#F2F8F5] border border-[#008057]/20 p-4 mb-6">
          <div className="font-bold text-[#03281B]">Sponsored Registration - Paid</div>
          <p className="text-sm text-slate-600">A sponsor has covered the camp fee for your child. Please complete the details below - there is nothing to pay.</p>
        </div>
        <div className="rounded-2xl bg-white border border-[#E2EBF0] p-6 space-y-6" data-testid="sponsored-register-form">
          <ParticipantFields p={p} setP={setP} sizes={ctx.clothing_sizes || { tshirt: [], shorts: [], socks: [] }} />
          <GuardianFields g={g} setG={setG} />
          <div className="space-y-2">
            {CONSENTS.map(([k, l]) => (
              <label key={k} className="flex items-start gap-2 text-sm cursor-pointer"><input data-testid={`sp-consent-${k}`} type="checkbox" className="accent-[#008057] mt-0.5" checked={!!c[k]} onChange={(e) => setC({ ...c, [k]: e.target.checked })} /> <span>{l} <span className="text-red-500">*</span></span></label>
            ))}
          </div>
          <button data-testid="sponsored-submit" onClick={submit} disabled={saving} className="w-full rounded-full bg-[#008057] text-white py-3 font-bold disabled:opacity-60">{saving ? "Submitting..." : "Complete Registration"}</button>
        </div>
      </div>
    </div>
  );
}
